import { useState } from "react";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { devicesService } from "@/services";

interface DeviceUnauthorizedModalProps {
  open: boolean;
  onClose: () => void;
  email: string;
  password?: string;
  ipAddress?: string;
  status: "REJECTED" | "REVOKED";
  rejectReason?: string | null;
  onRequestSubmitted: (details: { ipAddress?: string; requestedAt: string; browser?: string; os?: string }) => void;
}

export function DeviceUnauthorizedModal({
  open,
  onClose,
  email,
  password,
  ipAddress,
  status,
  rejectReason,
  onRequestSubmitted,
}: DeviceUnauthorizedModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestApproval = async () => {
    if (!password) {
      setError("Please re-enter your password on the login screen to request approval.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await devicesService.requestDeviceApproval({
        email,
        password,
        ipAddress,
      });

      onRequestSubmitted({
        ipAddress: res.ipAddress || ipAddress,
        requestedAt: new Date().toISOString(),
        browser: res.browser,
        os: res.os,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit approval request.");
    } finally {
      setSubmitting(false);
    }
  };

  const isRevoked = status === "REVOKED";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isRevoked ? "IP Access Revoked" : "IP Request Rejected"}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRequestApproval} loading={submitting}>
            Request Approval for this IP
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-xs text-foreground-600">
        <div className="flex items-start gap-3 rounded-lg border border-danger-200 bg-danger-50/60 p-3.5 text-danger-800">
          <i className="ri-error-warning-fill mt-0.5 text-lg text-danger-600" />
          <div className="space-y-1">
            <span className="font-semibold text-danger-900">
              {isRevoked
                ? "This IP address's access was previously revoked."
                : "This IP request was previously rejected."}
            </span>
            <p className="text-danger-700">
              {rejectReason ? `Reason provided: "${rejectReason}"` : "An administrator restricted access from this network location."}
            </p>
          </div>
        </div>

        <p>
          You cannot sign in from this IP address until an administrator grants approval. You can submit a new approval request below.
        </p>

        <div className="rounded-md border border-background-200 bg-background-100 p-3 space-y-1">
          <div className="text-[11px] font-medium text-foreground-500">System IP Address</div>
          <div className="break-all font-mono text-xs font-semibold text-foreground-800">{ipAddress || "Current IP"}</div>
        </div>

        {error ? (
          <div className="rounded-md border border-danger-200 bg-danger-50 p-2.5 text-danger-700">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
